/**
 * settingsMetaBase.js — o que cada parâmetro É (rótulo, descrição, tipo, unidade, limites)
 * CI Sistemas de Informática Ltda. — AcessoiD
 *
 * Onda 100: este bloco saiu de `pages/Settings.js` POR SCRIPT, sem redigitação — é
 * o mesmo texto, em outro arquivo. A única coisa removida foi o campo `section`:
 * ONDE cada parâmetro aparece deixou de ser propriedade dele e passou a viver em
 * `settingsEstrutura.js`. Reorganizar a tela não pode exigir tocar no que o
 * parâmetro é.
 */
export const META_BASE = {
  // Onda 123: QUAL cliente é esta instalação — a vertical diz só o tipo de operação.
  client_name: {
    label: 'Nome do cliente',
    description: 'O nome do cliente desta instalação, como a equipe o conhece — por exemplo "G2 Parking" ou "Condomínio Itaipava All Suítes". Aparece no topo do menu, na aba do navegador e na tela de login, para quem acessa mais de uma instalação saber em qual está. Vazio: só a marca e a vertical, como antes.',
    type: 'text', padrao: '', podeVazio: true, placeholder: 'G2 Parking',
    validar: (t) => (String(t || '').trim().length <= 80 ? null : 'Use até 80 caracteres: o nome tem de caber no topo do menu.'),
  },
  system_vertical: {
    label: 'Tipo de Operação',
    description: 'Define o perfil do sistema. Estacionamento Convencional habilita cobrança e tarifas. Condomínio habilita gestão de moradores e visitantes. Clube Social limita a plataforma ao reconhecimento e à integração com o sistema externo do clube.',
    type: 'select',
    options: [
      { value: 'estacionamento', label: '🏥 Estacionamento Convencional' },
      { value: 'condominio_residencial', label: '🏠 Condomínio Residencial' },
      { value: 'condominio_corporativo', label: '🏢 Condomínio Corporativo' },
      { value: 'clube_social', label: '🏛️ Clube Social (integração externa)' },
    ],
  },
  // Onda 154: cobrança de ticket fora do estacionamento (clube social, condomínio corporativo).
  cobranca_habilitada: {
    label: 'Cobrança habilitada',
    description: 'Liga a cobrança de ticket nesta instalação, mantendo os módulos próprios da vertical. Com ela ligada, aparecem no menu e na Central de Ajuda os módulos de cobrança — Tickets, Caixa, Tarifas, Clientes, Mensalistas, Credenciados, Convênios, Reconciliação, Quarentena, Inventário de Pátio, relatórios operacionais e financeiros, impressão e o app da guarita —, com as mesmas permissões por perfil do estacionamento. Só o superadministrador altera. No estacionamento a cobrança é a própria vertical, e esta chave não existe.',
    type: 'toggle', padrao: 'false',
  },
  parallel_mode: {
    label: 'Modo de Operação Paralela',
    description: 'Quando ativado, a cancela de saída é liberada automaticamente para veículos avulsos sem exigir pagamento prévio. O valor é calculado e registrado como "baixa automática". Use durante o período de transição com o sistema legado.',
    type: 'toggle',
  },
  single_camera_mode: {
    label: 'Modo Câmera Única (Entrada e Saída)',
    description: 'Ative quando uma única câmera monitora tanto a entrada quanto a saída do estacionamento. O sistema determina automaticamente se o veículo está entrando ou saindo com base no estado do ticket: sem ticket ativo → ENTRADA; com ticket ativo → SAÍDA com baixa automática. Não compatível com câmeras de condomínio.',
    type: 'toggle',
  },
  frame_interval: {
    label: 'Intervalo de Captura',
    description: 'Tempo entre cada captura de frame das câmeras. Valores menores aumentam a precisão, mas consomem mais recursos do servidor.',
    type: 'number',
    unit: 'ms',
    recommended: 2000,
  },
  dedup_window_entry: {
    label: 'Tempo Mínimo entre Entradas',
    description: 'Depois de registrar uma entrada, a mesma placa não é registrada de novo pela mesma câmera de entrada durante este período. Vale por câmera e por sentido; em produção a consolidação por passagem já agrupa as leituras de uma mesma passagem antes desta regra.',
    type: 'number',
    unit: 'ms',
    recommended: 150000,
  },
  dedup_window_exit: {
    label: 'Tempo Mínimo entre Saídas',
    description: 'Depois de registrar uma saída, a mesma placa não é registrada de novo pela mesma câmera de saída durante este período. Este mesmo período também bloqueia uma nova ENTRADA da placa (ou de uma variante de leitura) em qualquer outra câmera: é o que impede a câmera de entrada de registrar o carro que acabou de sair pela pista ao lado.',
    type: 'number',
    unit: 'ms',
    recommended: 150000,
  },
  // Onda 39: a zona de observação passa a ser ligada e ajustada pela tela.
  // O defeito do GBR5409 (saída registrada 7 s depois da entrada) mostrou que
  // desligar isso não pode depender de deploy.
  observation_zone_enabled: {
    label: 'Zona de Observação da Câmera de Entrada',
    description: 'Usa a segunda zona desenhada na câmera de entrada (aba "Zona de observação" no editor da tela Câmeras) para reconhecer veículos que saíram sem a câmera de saída ler. A leitura ali nunca abre ticket. Desligue para suspender a funcionalidade inteira sem mexer no desenho das zonas.',
    type: 'toggle',
  },
  observation_min_minutes_after_entry: {
    label: 'Carência da Zona de Observação',
    description: 'Tempo que precisa passar desde a entrada antes de a zona de observação poder reconhecer a saída do mesmo veículo. Sem carência, o carro que acabou de entrar avança alguns metros, cai na zona de observação e é lido como se estivesse saindo — foi o que baixou indevidamente o ticket do GBR5409 em 07/09/2026, sete segundos depois da entrada. Um veículo que sai dentro desta janela não gera receita de qualquer modo, porque a carência da tarifa zera o valor.',
    type: 'number',
    unit: 'min',
    recommended: 10,
  },
  observation_auto_close: {
    label: 'Baixa Automática pela Zona de Observação',
    description: 'Quando a zona de observação confirma que o veículo saiu, o ticket é baixado com o mesmo cálculo da câmera de saída. Desligue para apenas registrar o caso na tela Reconciliação e na Quarentena, deixando a baixa por conta do operador.',
    type: 'toggle',
  },
  observation_min_sightings: {
    label: 'Leituras para Confirmar a Saída',
    description: 'Quantas leituras da mesma placa a zona de observação exige antes de aceitar que o veículo está saindo. Com 2 ou mais, o sistema também verifica se o veículo se afasta ao longo das leituras, o que descarta carros parados. Com 1, a primeira leitura basta e a verificação de movimento não acontece.',
    type: 'number',
    unit: 'leituras',
    recommended: 2,
  },
  min_plate_length: {
    label: 'Comprimento Mínimo de Placa',
    description: 'Leituras com menos caracteres que este valor são descartadas como ruído ambiental (reflexos, sombras, letreiros).',
    type: 'number',
    unit: 'chars',
    recommended: 7,
  },
  // Onda 26 (F-22): o campo "min_confidence" foi removido — nada no backend o lia;
  // o limiar efetivo é o threshold por luminância do motor (seção ALPR Avançado).
  fastalpr_timeout: {
    label: 'Tempo Limite de Reconhecimento',
    description: 'Tempo máximo que o sistema aguarda a resposta do motor de reconhecimento antes de descartar o frame. Deve ser maior que o limite interno do motor (INFERENCE_TIMEOUT, 4 s), para que o motor desista antes e a fila não acumule frames velhos. Um frame leva de 0,03 a 0,08 s em condições normais.',
    type: 'number',
    unit: 'ms',
    recommended: 5000,
  },
  burst_frames: {
    label: 'Frames por Detecção (Burst)',
    description: '⚠️ Funcionalidade temporariamente desabilitada. O modo burst está em manutenção e será reativado em uma próxima versão. Mantenha o valor em 1.',
    type: 'number',
    unit: 'frames',
    recommended: 1,
    disabled: true,
  },
  burst_interval_ms: {
    label: 'Intervalo entre Frames do Burst',
    description: '⚠️ Funcionalidade temporariamente desabilitada. Será reativada junto com o modo burst em uma próxima versão.',
    type: 'number',
    unit: 'ms',
    recommended: 300,
    disabled: true,
  },
  max_parking_duration: {
    label: 'Janela de Abandono',
    description: 'Ticket ativo sem saída acima deste tempo é marcado como abandonado pela tarefa horária (finalizado, sem saída). Ele continua sendo encontrado na saída (placa exata até 7 dias, variante de OCR até 3 dias) para cobrar a permanência real. Em hospital, pernoites são comuns: 72 h é um valor adequado.',
    type: 'number',
    unit: 'ms',
    inputUnit: 'h',   // o operador informa horas; o valor é gravado em ms (chave histórica)
    step: 1,
    recommended: 259200000,
  },
  exit_tolerance_min: {
    label: 'Tolerância de Saída após Pagamento',
    description: 'Tempo que o cliente tem para sair após efetuar o pagamento no caixa, sem gerar cobrança adicional.',
    type: 'number',
    unit: 'min',
    recommended: 15,
  },
  // Onda 27: "Taxa de Pernoite" e "Início do Pernoite" saíram da tela — nenhum
  // código lia essas chaves; a regra de pernoite fica para quando o produto
  // tiver essa cobrança implementada.
  webhook_url: {
    label: 'URL do Webhook de Cancela',
    description: 'Endereço HTTP/HTTPS que receberá as notificações de abertura de cancela. Deve ser um endpoint POST que aceite JSON.',
    type: 'url',
    placeholder: 'https://meu-sistema.com/api/webhook/cancela',
  },
  // ── Threshold Dinâmico Noturno (Onda 3) ─────────────────────────────────
  dynamic_threshold_enabled: {
    label: 'Threshold Dinâmico Noturno',
    description: 'Quando habilitado, o sistema ajusta automaticamente a confiança mínima de reconhecimento de acordo com o horário do dia. Baseado em análise de 4.841 snapshots históricos: à noite a confiança média cai de 0,956 (diurno) para 0,691 (madrugada), exigindo um limiar menor para não descartar leituras válidas.',
    type: 'toggle',
    recommended: true,
  },
  confidence_threshold_day: {
    label: 'Confiança Mínima — Diurno (luz alta)',
    description: 'Limiar de confiança aplicado quando o brilho medido na zona de leitura está acima da faixa de crepúsculo (desde a Onda 24 o período vem da luz medida a cada frame, não do relógio). Condições de iluminação ideais permitem um valor mais alto, descartando mais ruídos.',
    type: 'number',
    unit: '',
    recommended: 0.60,
    min: 0.30,
    max: 0.95,
    step: 0.01,
  },
  confidence_threshold_dusk: {
    label: 'Confiança Mínima — Crepúsculo (luz média)',
    description: 'Limiar de confiança aplicado quando o brilho medido na zona fica na faixa intermediária (entardecer, amanhecer, chuva forte). Iluminação variável exige um valor ligeiramente menor que o diurno.',
    type: 'number',
    unit: '',
    recommended: 0.57,
    min: 0.30,
    max: 0.95,
    step: 0.01,
  },
  confidence_threshold_night: {
    label: 'Confiança Mínima — Noturno (luz baixa)',
    description: 'Limiar de confiança aplicado quando o brilho medido na zona está na faixa noturna. Iluminação artificial e faróis diretos reduzem a qualidade das leituras; um valor menor evita que placas válidas sejam descartadas, e a consolidação por passagem compensa a aceitação de leituras com menor confiança.',
    type: 'number',
    unit: '',
    recommended: 0.52,
    min: 0.30,
    max: 0.95,
    step: 0.01,
  },
  overexp_enabled: {
    label: 'Correção de Superexposição',
    description: '⚠️ Funcionalidade em manutenção — desabilitada temporariamente. A lógica de detecção de brilho por ROI não distingue corretamente faróis noturnos de superexposição diurna, podendo degradar frames e reduzir a taxa de reconhecimento.',
    type: 'toggle',
    recommended: false,
    disabled: true,
  },
  overexp_threshold: {
    label: 'Limiar de Superexposição',
    description: '⚠️ Funcionalidade em manutenção — parâmetro bloqueado.',
    type: 'number',
    unit: '',
    recommended: 200,
    min: 150,
    max: 250,
    step: 5,
    disabled: true,
  },
  overexp_gamma: {
    label: 'Gamma de Correção',
    description: '⚠️ Funcionalidade em manutenção — parâmetro bloqueado.',
    type: 'number',
    unit: '',
    recommended: 0.40,
    min: 0.10,
    max: 0.90,
    step: 0.05,
    disabled: true,
  },
  // ── Armazenamento ─────────────────────────────────────────────────────────
  snapshot_retention_days: {
    label: 'Retenção de Evidências Fotográficas',
    description: 'Período de armazenamento das fotos capturadas pelas câmeras. Após o prazo configurado, as imagens são excluídas automaticamente pelo sistema durante a madrugada. Valores menores economizam espaço em disco; valores maiores permitem auditorias mais longas.',
    type: 'select',
    options: [
      { value: '7',   label: '1 semana (7 dias)' },
      { value: '14',  label: '2 semanas (14 dias)' },
      { value: '21',  label: '3 semanas (21 dias)' },
      { value: '28',  label: '4 semanas (28 dias)' },
      { value: '60',  label: '2 meses (60 dias)' },
      { value: '90',  label: '3 meses (90 dias)' },
      { value: '120', label: '4 meses (120 dias)' },
      { value: '180', label: '6 meses (180 dias)' },
    ],
  },
  crop_retention_days: {
    label: 'Retenção dos Recortes de Placa',
    description: 'Os recortes de placa e os metadados de cada leitura (cerca de 8 KB por leitura) formam o corpus para o ajuste fino do OCR e podem ser guardados por muito mais tempo que as fotos. Com menos de 40 GB livres no disco, a rede de segurança aplica a eles a mesma retenção das fotos.',
    type: 'select',
    options: [
      { value: '90',  label: '3 meses (90 dias)' },
      { value: '365', label: '1 ano (365 dias)' },
      { value: '730', label: '2 anos (730 dias)' },
      { value: '0',   label: 'Permanentes (nunca apagar)' },
    ],
  },
  min_bbox_width: {
    label: 'Largura Mínima da Placa na Imagem',
    description: 'Leituras com a placa mais estreita que este valor (em pixels do frame de 1280 px de largura) são descartadas mesmo com confiança alta: são carros ao fundo ou na rua. Medição de 06/09: placas válidas entre 64 e 224 px (mediana 134); carros estacionados ao fundo entre 50 e 79 px. Use 0 para desativar.',
    type: 'number',
    unit: 'px',
    recommended: 50,
  },
  // ── Onda 27: paridade com o motor (todo parâmetro que o motor lê do .env) ──
  threshold_mode: {
    label: 'Como escolher a faixa do limiar',
    description: 'hybrid: pelo brilho medido na zona a cada frame, com o relógio como reserva (recomendado). luminance: só pelo brilho. clock: só pelo relógio, como era antes da Onda 24.',
    type: 'select',
    options: [{ value: 'hybrid', label: 'Brilho medido, relógio como reserva (hybrid)' }, { value: 'luminance', label: 'Só brilho medido (luminance)' }, { value: 'clock', label: 'Só relógio (clock)' }],
  },
  luminance_night_max: {
    label: 'Brilho máximo da faixa noturna',
    description: 'Brilho médio (0 a 255) da metade inferior da zona até o qual vale o limiar noturno. Medido na câmera 3: 67 a 81 à noite, com a iluminação do pátio.',
    type: 'number', unit: '', recommended: 90, step: 1,
  },
  luminance_dusk_max: {
    label: 'Brilho máximo da faixa de crepúsculo',
    description: 'Brilho médio até o qual vale o limiar de crepúsculo; acima vale o diurno. Medido na câmera 3: 112 a 130 de dia; a transição da manhã fica entre 08 h e 09 h.',
    type: 'number', unit: '', recommended: 105, step: 1,
  },
  luminance_ema_alpha: {
    label: 'Suavização do brilho medido',
    description: 'Média móvel exponencial do brilho por câmera antes de escolher a faixa. 0,1 equivale a cerca de 10 frames (10 s a 1 frame/s); 1 desliga a suavização e o período pode oscilar a cada frame.',
    type: 'number', unit: '', recommended: 0.1, step: 0.05,
  },
  luminance_hysteresis: {
    label: 'Histerese na troca de faixa',
    description: 'O período só muda quando o brilho suavizado ultrapassa a fronteira em mais que este valor. Com 4, um brilho oscilando entre 101 e 107 em torno da fronteira de 105 não fica alternando diurno e crepúsculo.',
    type: 'number', unit: '', recommended: 4, step: 1,
  },
  luminance_overrides: {
    label: 'Faixas de brilho por câmera (JSON)',
    description: 'Câmeras cuja zona mede brilho diferente da referência usam faixas próprias: {"4":{"night_max":80,"dusk_max":92}}. Câmera 4 medida em 06/09: noite 63 a 78, dia 90 a 113. Câmeras sem entrada usam as faixas globais acima. JSON inválido não é aplicado.',
    type: 'text',
  },
  min_char_confidence: {
    label: 'Confiança mínima por caractere',
    description: 'Leitura com algum caractere abaixo deste valor é descartada e o carro é lido de novo no frame seguinte. Valor alto elimina trocas de caractere mas pode atrasar o registro de placas sujas ou mal iluminadas.',
    type: 'number', unit: '', recommended: 0.70, step: 0.01,
  },
  plate_crop_margin: {
    label: 'Margem do recorte da placa',
    description: 'Fração do bbox acrescentada ao recorte que o OCR lê. Na homologação de 05/09, 5% e 10% pioraram o acerto; manter 0.',
    type: 'number', unit: 'fração', recommended: 0, step: 0.01,
  },
  ocr_source: {
    label: 'Frame usado pelo OCR',
    description: 'raw: o OCR recorta a placa do frame original (homologado). preprocessed: recorta do frame com denoise e CLAHE, que altera os caracteres e piorou o acerto na homologação.',
    type: 'select', options: [{ value: 'raw', label: 'Frame original (raw)' }, { value: 'preprocessed', label: 'Frame pré-processado' }],
  },
  denoise_mode: {
    label: 'Redução de ruído na zona',
    description: 'bilateral: 4 ms por frame, acerto igual ao nlmeans (homologado). nlmeans: 480 a 919 ms por frame e 99% de CPU. none: sem redução de ruído.',
    type: 'select', options: [{ value: 'bilateral', label: 'Bilateral (4 ms)' }, { value: 'nlmeans', label: 'NLMeans (lento)' }, { value: 'none', label: 'Nenhuma' }],
  },
  denoise_search_window: {
    label: 'Janela de busca do NLMeans',
    description: 'Só tem efeito no modo nlmeans. Janelas maiores são mais lentas.',
    type: 'select', options: [{ value: '7', label: '7' }, { value: '11', label: '11' }, { value: '15', label: '15' }, { value: '21', label: '21' }],
  },
  layout_classifier_enabled: {
    label: 'Classificador de layout (Mercosul × antiga)',
    description: 'Desempata a quinta posição da placa (letra no Mercosul, dígito na antiga) quando o OCR está inseguro nela, pela aparência do recorte. Só atua com o classificador confiante e o OCR abaixo da confiança máxima configurada.',
    type: 'toggle', recommended: true,
  },
  layout_min_conf: {
    label: 'Confiança mínima do classificador de layout',
    description: 'O desempate só ocorre quando a probabilidade estimada de Mercosul é maior ou igual a este valor (ou menor ou igual ao complemento, para antiga).',
    type: 'number', unit: '', recommended: 0.90, step: 0.01,
  },
  layout_ocr_max_conf: {
    label: 'Confiança máxima do OCR para desempatar',
    description: 'O desempate só ocorre quando a confiança do OCR na quinta posição é menor que este valor. Com 0,90, leituras seguras nunca são alteradas.',
    type: 'number', unit: '', recommended: 0.90, step: 0.01,
  },
  save_plate_crops: {
    label: 'Gravar recorte e metadados de cada leitura',
    description: 'Cerca de 8 KB por leitura. É o corpus do ajuste fino do OCR e alimenta a tela Qualidade do OCR; manter ligado.',
    type: 'toggle', recommended: true,
  },
  save_discarded_evidence: {
    label: 'Gravar foto de leituras descartadas pela zona',
    description: 'Quando nenhuma detecção cai na zona mas há uma leitura válida fora dela, grava a foto com o sufixo forazona para auditoria (caso CDK9888). Limitada por placa e câmera pelo intervalo abaixo.',
    type: 'toggle', recommended: true,
  },
  discarded_min_conf: {
    label: 'Confiança mínima da leitura descartada',
    description: 'Só leituras descartadas com confiança maior ou igual a este valor geram foto.',
    type: 'number', unit: '', recommended: 0.90, step: 0.01,
  },
  discarded_near_zone: {
    label: 'Foto de descartada só perto da zona',
    description: 'Só grava a foto quando o centro da placa está dentro da zona expandida por esta fração (0,15 = 15% da largura e da altura). O trânsito da rua ao fundo fica de fora; um carro real na borda da zona (caso CDK9888) continua sendo fotografado. 0 restringe ao envolvente exato.',
    type: 'number', unit: 'fração', recommended: 0.15, step: 0.05,
  },
  discarded_throttle_s: {
    label: 'Intervalo entre fotos descartadas da mesma placa',
    description: 'Segundos entre fotos da mesma placa na mesma câmera. Com 1800, um carro estacionado ao fundo gera 48 fotos por dia em vez de 86 mil.',
    type: 'number', unit: 's', recommended: 1800, step: 60,
  },
  snapshot_quality: {
    label: 'Qualidade JPEG das fotos',
    description: 'De 30 a 100. Com 85, uma foto em 1080p tem cerca de 260 KB.',
    type: 'number', unit: '', recommended: 85, step: 1,
  },
  inference_timeout: {
    label: 'Tempo limite interno do motor',
    description: 'Segundos que o motor espera por uma inferência antes de desistir. Deve ser menor que o tempo limite de reconhecimento do backend (acima, em ms), para que a fila não acumule frames velhos.',
    type: 'number', unit: 's', recommended: 4, step: 1,
  },
  night_start_hour: { label: 'Relógio (reserva): início da noite', description: 'Usado só quando não há brilho medido ou no modo clock.', type: 'number', unit: 'h', recommended: 22, step: 1 },
  night_end_hour: { label: 'Relógio (reserva): fim da noite', description: 'Usado só quando não há brilho medido ou no modo clock.', type: 'number', unit: 'h', recommended: 5, step: 1 },
  dusk_morning_start_hour: { label: 'Relógio (reserva): início do crepúsculo da manhã', description: 'Usado só quando não há brilho medido ou no modo clock.', type: 'number', unit: 'h', recommended: 5, step: 1 },
  dusk_morning_end_hour: { label: 'Relógio (reserva): fim do crepúsculo da manhã', description: 'Usado só quando não há brilho medido ou no modo clock.', type: 'number', unit: 'h', recommended: 6, step: 1 },
  dusk_evening_start_hour: { label: 'Relógio (reserva): início do crepúsculo da tarde', description: 'Usado só quando não há brilho medido ou no modo clock.', type: 'number', unit: 'h', recommended: 18, step: 1 },
  dusk_evening_end_hour: { label: 'Relógio (reserva): fim do crepúsculo da tarde', description: 'Usado só quando não há brilho medido ou no modo clock.', type: 'number', unit: 'h', recommended: 22, step: 1 },
};
